import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react"
import { useClients } from "@/hooks"
import { Lock } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { UseFieldEditorReturn } from "@/hooks/useFieldEditor"
import {
  EDITABLE_FIELD_ATTR,
  FOCUS_TARGET_ATTR
} from "../focusConstants"
import type { CurrencyRepresentation, ExtractedField, FieldOption, Invoice, LineItemGroups } from "../types"
import { REQUIRED_INVOICE_FIELDS } from "../fieldConstants"
import { useInvoiceFieldCategories } from "../hooks/useInvoiceFieldCategories"
import { useInvoiceType } from "../hooks/useInvoiceType"
import { useErpUnitsOfMeasure } from "../hooks/useErpOptions"
import { InvoiceFieldInput } from "./InvoiceFieldsPanel/InvoiceFieldInput"
import { LineItemsCard } from "./InvoiceFieldsPanel/LineItemsCard"
import { OriginalDocPicker } from "./InvoiceFieldsPanel/OriginalDocPicker"
import { CounterpartyAssignmentCard } from "./CounterpartyAssignmentCard"
import { RedesignedCurrencyField } from "./RedesignedCurrencyField"
import { apiClient } from "@/lib/api"
import { OriginalValueIndicator } from "./OriginalValueIndicator"
import { ViesValidationBadge, getViestooltipText } from "./ViesValidationBadge"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  computeDraftFromRows,
  computeErpDraftFromRows,
  createEditableValueRenderer,
  createEmptyDraft,
  formatFieldDisplayValue,
  getEditableValue,
  type LineItemDraft,
  type LineItemErpDraft,
  type LineItemRow,
  type SerializedLineItem,
  trimValue
} from "../utils/invoiceFieldHelpers"
import { MergeDrawerErpFields } from "./InvoiceFieldsPanel/MergeDrawerErpFields"

export interface LineItemAction {
  type: "merge" | "create" | "delete"
  nextLineItems: SerializedLineItem[]
  targetIds?: string[]
}

type DrawerMode = "merge" | "create"

interface LineItemErpMetadata {
  deal_name?: number | null
  acc_assignment?: number | null
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
  without_account_assignment?: boolean
  without_account_assignment_reason?: "dma_auto" | "manual" | null
}

interface InvoiceFieldsPanelProps {
  invoice: Invoice
  onFieldUpdate?: (fieldId: string, newValue: string) => void
  onFieldPatch?: (updatedField: Record<string, unknown>) => void
  onInvoiceRefresh?: () => void
  onRepresentationUpdate?: (updatedRepr: CurrencyRepresentation) => void
  fieldEditor: UseFieldEditorReturn
  onLineItemsAction?: (action: LineItemAction) => Promise<void> | void
  onLineItemErpMetadataChange?: (lineItemGroup: string, metadata: LineItemErpMetadata) => void
  onLineItemsValidityChange?: (missingLabels: string[]) => void
  onRestoreLineItems?: (mergedGroupId: string) => Promise<void> | void
}

export const InvoiceFieldsPanel = ({
  invoice,
  onFieldUpdate,
  onFieldPatch,
  onInvoiceRefresh,
  onRepresentationUpdate,
  fieldEditor,
  onLineItemsAction,
  onLineItemErpMetadataChange,
  onLineItemsValidityChange,
  onRestoreLineItems
}: InvoiceFieldsPanelProps): JSX.Element => {
  const categorizedData = useInvoiceFieldCategories(invoice)
  const { clients } = useClients()

  // Resolve full client details (including vat_code) which might be missing from invoice.client
  const fullClient = useMemo(() => {
    if (!invoice.client?.id) return invoice.client
    return clients.find(c => c.id === invoice.client?.id) || invoice.client
  }, [clients, invoice.client])

  const { isIncome } = useInvoiceType({
    client: fullClient,
    vendorFields: categorizedData.vendor
  })

  // Fields that are locked (read-only) when a counterparty is selected
  const LOCKED_COUNTERPARTY_FIELDS = new Set([
    "vendor_name", "vendor_address", "vendor_tax_id", "vendor_country",
    "customer_name", "customer_address", "customer_tax_id", "customer_country"
  ])

  const hasCounterparty = invoice.mirage_counterparty_id != null
  const isExported = invoice.status === "exported"

  const isFieldLockedByCounterparty = useCallback(
    (fieldName: string) => {
      return hasCounterparty && LOCKED_COUNTERPARTY_FIELDS.has(fieldName.toLowerCase())
    },
    [hasCounterparty]
  )

  // VIES VAT validation state
  const [validatingFieldIds, setValidatingFieldIds] = useState<Set<string>>(new Set())
  const triggerViesValidation = useCallback(
    async (fieldId: string) => {
      setValidatingFieldIds(prev => new Set(prev).add(fieldId))
      try {
        const result = await apiClient.post<{ vies_validation: unknown; field: Record<string, unknown> }>(
          `/invoices/${invoice.id}/fields/${fieldId}/validate-vat`
        )
        // Patch only extra_metadata from VIES — preserve the current field_value
        if (result?.field) {
          const viesField = result.field as Record<string, unknown>
          onFieldPatch?.({
            id: viesField.id,
            extra_metadata: viesField.extra_metadata
          })
        }
      } catch (err) {
        console.error("VIES validation failed:", err)
      } finally {
        setValidatingFieldIds(prev => {
          const next = new Set(prev)
          next.delete(fieldId)
          return next
        })
      }
    },
    [invoice.id, onFieldPatch]
  )

  const handleFieldUpdateWithVies = useCallback(
    async (fieldId: string, newValue: string) => {
      // Await the PUT so the value is committed before VIES reads it
      await Promise.resolve(onFieldUpdate?.(fieldId, newValue))

      // If this is a tax_id field, trigger VIES validation after the save completes
      const field = invoice.extractedData.find(f => f.id === fieldId)
      if (!field) return
      const fieldName = field.field_name.toLowerCase()
      if (fieldName === "vendor_tax_id" || fieldName === "customer_tax_id") {
        triggerViesValidation(fieldId)
      }
    },
    [onFieldUpdate, invoice.extractedData, triggerViesValidation]
  )

  const handleSelectFieldChange = useCallback(
    async (field: ExtractedField, value: string) => {
      if (!handleFieldUpdateWithVies) {
        return
      }

      const normalizedValue = value === "__clear" ? "" : value
      const currentValue = field.field_value ?? ""
      if (currentValue === normalizedValue) {
        return
      }

      await Promise.resolve(handleFieldUpdateWithVies(field.id, normalizedValue))
    },
    [handleFieldUpdateWithVies]
  )

  const groupedLineItems = useMemo<LineItemGroups>(() => {
    const groups: LineItemGroups = {}

    categorizedData.lineItems.forEach((field) => {
      const groupId = field.line_item_group || "ungrouped"
      if (!groups[groupId]) {
        groups[groupId] = []
      }
      groups[groupId].push(field)
    })

    const sortedGroups: LineItemGroups = {}
    Object.keys(groups)
      .sort((a, b) => {
        if (a === "ungrouped") return 1
        if (b === "ungrouped") return -1
        return a.localeCompare(b, undefined, { numeric: true })
      })
      .forEach((key) => {
        sortedGroups[key] = groups[key]
      })

    return sortedGroups
  }, [categorizedData.lineItems])

  const activeRepr = invoice.currency_representations?.find(
    (r) => r.currency_code === invoice.active_currency_code
  )
  const reprLineItems = activeRepr?.line_items ?? []

  const lineItemRows = useMemo<LineItemRow[]>(() => {
    return Object.entries(groupedLineItems).map(([groupId, fields]) => {
      const fieldMap = fields.reduce<Record<string, ExtractedField>>((acc, field) => {
        acc[field.field_name] = field
        return acc
      }, {})

      const reprItem = reprLineItems.find((li) => li.line_item_group === groupId)

      return {
        id: groupId,
        fields,
        description: fieldMap.line_item_description,
        unitOfMeasure: fieldMap.line_item_unit_of_measure,
        quantity: reprItem?.quantity ?? null,
        unitPrice: reprItem?.unit_price ?? null,
        lineTotal: reprItem?.line_total ?? null,
        lineTax: reprItem?.line_tax ?? null,
        vatRate: reprItem?.vat_rate ?? null,
        discountPercent: reprItem?.discount_percent ?? null,
        discountAmount: reprItem?.discount_amount ?? null,
      }
    })
  }, [groupedLineItems, reprLineItems])

  // Extract client ID and line items ERP metadata from invoice
  const clientId = invoice.client?.id
  const { units: erpUnits } = useErpUnitsOfMeasure(clientId)
  const serverLineItemsErpMetadata = useMemo<Record<string, LineItemErpMetadata>>(() => {
    const erpLineItems = invoice.additional_metadata?.erp?.line_items
    if (!Array.isArray(erpLineItems)) {
      return {}
    }
    return erpLineItems.reduce<Record<string, LineItemErpMetadata>>((acc, item) => {
      if (item.line_item_group) {
        acc[item.line_item_group] = {
          deal_name: item.deal_name,
          acc_assignment: item.acc_assignment,
          analytic_parts: item.analytic_parts,
          without_account_assignment: item.without_account_assignment,
        }
      }
      return acc
    }, {})
  }, [invoice.additional_metadata?.erp?.line_items])

  // Local optimistic state for immediate UI feedback
  const [lineItemsErpMetadata, setLineItemsErpMetadata] = useState(serverLineItemsErpMetadata)

  // Sync from server when invoice refreshes
  useEffect(() => {
    setLineItemsErpMetadata(serverLineItemsErpMetadata)
  }, [serverLineItemsErpMetadata])

  // Wrap onLineItemErpMetadataChange to also update local state optimistically
  const handleLineItemErpMetadataChange = useCallback(
    (lineItemGroup: string, metadata: LineItemErpMetadata) => {
      setLineItemsErpMetadata((prev) => ({
        ...prev,
        [lineItemGroup]: metadata,
      }))
      onLineItemErpMetadataChange?.(lineItemGroup, metadata)
    },
    [onLineItemErpMetadataChange]
  )

  // Build a set of merged group IDs from merge history for quick lookup
  const mergedGroupIds = useMemo<Set<string>>(() => {
    const history = invoice.additional_metadata?.merge_history
    if (!Array.isArray(history)) return new Set()
    return new Set(history.map((h: any) => h.merged_group_id).filter(Boolean))
  }, [invoice.additional_metadata?.merge_history])

  const [selectedLineItemIds, setSelectedLineItemIds] = useState<string[]>([])
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null)
  const [draftValues, setDraftValues] = useState<LineItemDraft>(createEmptyDraft())
  const [pendingActionType, setPendingActionType] = useState<"merge" | null>(null)
  const areAllLineItemsSelected =
    lineItemRows.length > 0 && selectedLineItemIds.length === lineItemRows.length

  const selectedRows = useMemo(
    () => lineItemRows.filter((row) => selectedLineItemIds.includes(row.id)),
    [lineItemRows, selectedLineItemIds]
  )

  const hasSelection = selectedLineItemIds.length > 0
  const hasMergeableSelection = selectedLineItemIds.length >= 2
  const isMergePending = pendingActionType === "merge"

  const clearSelection = useCallback(() => {
    setSelectedLineItemIds([])
  }, [])

  const toSerializedLineItem = useCallback((row: LineItemRow): SerializedLineItem => {
    const description = row.description?.field_value ?? ""
    const quantity = row.quantity ?? ""
    const unitPrice = row.unitPrice ?? ""
    const total = row.lineTotal ?? ""
    const unit = row.unitOfMeasure?.field_value ?? ""

    const serialized: SerializedLineItem = {
      source_group_id: row.id,
      description: trimValue(description),
      quantity: trimValue(quantity),
      unit_price: trimValue(unitPrice),
      line_total: trimValue(total),
      vat_rate: row.vatRate ?? undefined,
      discount_percent: row.discountPercent ?? undefined,
      discount_amount: row.discountAmount ?? undefined,
    }

    const trimmedUnit = trimValue(unit)
    if (trimmedUnit !== "") {
      serialized.unit = trimmedUnit
    }

    return serialized
  }, [])

  const currentSerializedLineItems = useMemo(() => lineItemRows.map(toSerializedLineItem), [lineItemRows, toSerializedLineItem])

  const buildMergedLineItems = useCallback(
    (draft: LineItemDraft, targetIds: string[]): SerializedLineItem[] => {
      const targetSet = new Set(targetIds)
      const remaining = currentSerializedLineItems.filter(
        (item) => !item.source_group_id || !targetSet.has(item.source_group_id)
      )

      const mergedItem: SerializedLineItem = {
        source_group_id: targetIds[0] ?? `merged-${Date.now()}`,
        description: trimValue(draft.description),
        quantity: trimValue(draft.quantity),
        unit_price: trimValue(draft.unitPrice),
        line_total: trimValue(draft.total),
        vat_rate: trimValue(draft.vatRate) || undefined
      }

      const mergedUnit = trimValue(draft.unit)
      if (mergedUnit !== "") {
        mergedItem.unit = mergedUnit
      }

      const mergedDiscountAmount = trimValue(draft.discountAmount)
      if (mergedDiscountAmount !== "") {
        mergedItem.discount_amount = mergedDiscountAmount
      }
      const mergedDiscountPercent = trimValue(draft.discountPercent)
      if (mergedDiscountPercent !== "") {
        mergedItem.discount_percent = mergedDiscountPercent
      }

      if (targetIds.length > 0) {
        mergedItem.merged_from = targetIds
      }

      return [...remaining, mergedItem]
    },
    [currentSerializedLineItems]
  )

  const buildCreatedLineItems = useCallback(
    (draft: LineItemDraft): SerializedLineItem[] => {
      const newItem: SerializedLineItem = {
        source_group_id: `custom-${Date.now()}`,
        description: trimValue(draft.description),
        quantity: trimValue(draft.quantity),
        unit_price: trimValue(draft.unitPrice),
        line_total: trimValue(draft.total),
        vat_rate: trimValue(draft.vatRate) || undefined
      }

      const unitValue = trimValue(draft.unit)
      if (unitValue !== "") {
        newItem.unit = unitValue
      }

      return [...currentSerializedLineItems, newItem]
    },
    [currentSerializedLineItems]
  )

  const buildDeletedLineItems = useCallback(
    (targetId: string): SerializedLineItem[] =>
      currentSerializedLineItems.filter((item) => item.source_group_id !== targetId),
    [currentSerializedLineItems]
  )

  const handleRowSelectionChange = useCallback((rowId: string, checked: boolean) => {
    setSelectedLineItemIds((prev) => {
      if (checked) {
        if (prev.includes(rowId)) {
          return prev
        }
        return [...prev, rowId]
      }

      return prev.filter((id) => id !== rowId)
    })
  }, [])

  const handleSelectAllLineItems = useCallback(
    (checked: boolean) => {
      if (checked) {
        setSelectedLineItemIds(lineItemRows.map((row) => row.id))
        return
      }

      setSelectedLineItemIds([])
    },
    [lineItemRows]
  )

  const handleDeleteRow = useCallback(
    async (rowId: string) => {
      setSelectedLineItemIds((prev) => prev.filter((id) => id !== rowId))

      if (!onLineItemsAction) {
        return
      }

      const nextLineItems = buildDeletedLineItems(rowId)

      try {
        await onLineItemsAction({
          type: "delete",
          nextLineItems,
          targetIds: [rowId]
        })
      } catch (error) {
        console.error("Failed to delete line item:", error)
      }
    },
    [buildDeletedLineItems, onLineItemsAction]
  )

  const openDrawerWithDraft = useCallback((mode: DrawerMode, draft: LineItemDraft) => {
    setDraftValues(draft)
    setDrawerMode(mode)
    setIsDrawerOpen(true)
  }, [])

  const handleAddLineItem = useCallback(() => {
    clearSelection()
    openDrawerWithDraft("create", createEmptyDraft())
  }, [clearSelection, openDrawerWithDraft])

  const handleMergeSelected = useCallback(() => {
    if (!hasMergeableSelection || isMergePending) {
      return
    }

    const draft = computeDraftFromRows(selectedRows)
    const erpDraft = computeErpDraftFromRows(selectedLineItemIds, lineItemsErpMetadata)
    openDrawerWithDraft("merge", { ...draft, erp: erpDraft })
  }, [hasMergeableSelection, isMergePending, openDrawerWithDraft, selectedRows, selectedLineItemIds, lineItemsErpMetadata])

  const resetDrawerState = useCallback(() => {
    setDrawerMode(null)
    setDraftValues(createEmptyDraft())
  }, [])

  useEffect(() => {
    clearSelection()
    resetDrawerState()
    setIsDrawerOpen(false)
    setPendingActionType(null)
  }, [invoice.id, clearSelection, resetDrawerState])

  const handleDrawerOpenChange = useCallback(
    (open: boolean) => {
      if (pendingActionType) {
        return
      }

      setIsDrawerOpen(open)
      if (!open) {
        resetDrawerState()
      }
    },
    [pendingActionType, resetDrawerState]
  )

  const handleDraftFieldChange = useCallback((field: keyof LineItemDraft, value: string) => {
    setDraftValues((prev) => ({
      ...prev,
      [field]: value
    }))
  }, [])

  const handleErpDraftChange = useCallback((erpDraft: LineItemErpDraft) => {
    setDraftValues((prev) => ({
      ...prev,
      erp: erpDraft
    }))
  }, [])

  const handleDrawerSubmit = useCallback(async () => {
    if (!drawerMode) {
      setIsDrawerOpen(false)
      resetDrawerState()
      return
    }

    if (!onLineItemsAction) {
      if (drawerMode === "merge") {
        clearSelection()
      }
      setIsDrawerOpen(false)
      resetDrawerState()
      return
    }

    if (drawerMode === "merge") {
      if (selectedRows.length === 0) {
        return
      }

      const targetIds = selectedRows.map((row) => row.id)
      const nextLineItems = buildMergedLineItems(draftValues, targetIds)

      setPendingActionType("merge")
      let shouldClose = false

      try {
        await onLineItemsAction({
          type: "merge",
          nextLineItems,
          targetIds
        })
        // Save ERP metadata for the merged line item
        if (handleLineItemErpMetadataChange && draftValues.erp && (draftValues.erp.deal_name || draftValues.erp.acc_assignment)) {
          const mergedGroupId = targetIds[0] ?? `merged-${Date.now()}`
          handleLineItemErpMetadataChange(mergedGroupId, {
            deal_name: draftValues.erp.deal_name,
            acc_assignment: draftValues.erp.acc_assignment,
            analytic_parts: draftValues.erp.analytic_parts
          })
        }
        shouldClose = true
        clearSelection()
      } catch (error) {
        console.error("Failed to merge line items:", error)
      } finally {
        setPendingActionType(null)
        if (shouldClose) {
          setIsDrawerOpen(false)
          resetDrawerState()
        }
      }

      return
    }

    if (drawerMode === "create") {
      const nextLineItems = buildCreatedLineItems(draftValues)
      let shouldClose = false

      try {
        await onLineItemsAction({
          type: "create",
          nextLineItems
        })
        shouldClose = true
      } catch (error) {
        console.error("Failed to add line item:", error)
      } finally {
        if (shouldClose) {
          setIsDrawerOpen(false)
          resetDrawerState()
        }
      }
    }
  }, [
    buildCreatedLineItems,
    buildMergedLineItems,
    clearSelection,
    drawerMode,
    draftValues,
    onLineItemsAction,
    handleLineItemErpMetadataChange,
    resetDrawerState,
    selectedRows
  ])

  useEffect(() => {
    setSelectedLineItemIds((prev) =>
      prev.filter((id) => lineItemRows.some((row) => row.id === id))
    )
  }, [lineItemRows])

  const mergeConflictMessages = useMemo(() => {
    if (drawerMode !== "merge") {
      return []
    }

    const descriptions = new Set(
      selectedRows
        .map((row) => (row.description?.field_value ?? "").trim())
        .filter((value) => value !== "")
    )

    const units = new Set(
      selectedRows
        .map((row) => (row.unitOfMeasure?.field_value ?? "").trim())
        .filter((value) => value !== "")
    )

    const messages: string[] = []

    if (descriptions.size > 1) {
      messages.push("Selected rows have different descriptions. The merged description will overwrite them.")
    }

    if (units.size > 1) {
      messages.push("Selected rows use different units. Confirm the merged unit is correct.")
    }

    return messages
  }, [drawerMode, selectedRows])

  const fieldOptions = invoice.additional_metadata?.field_options
  const messageTypeOptions = (fieldOptions?.message_type ?? []) as FieldOption[]
  const documentSubjectOptions = (fieldOptions?.document_subject ?? []) as FieldOption[]

  // Detect credit/debit notes by resolving the current message_type to a
  // DocKind option and checking its Code (5 = debit note, 6 = credit note).
  const isCreditOrDebitNote = useMemo(() => {
    const currentMessageType = invoice.extractedData?.find(
      (f) => f.field_name === "message_type"
    )?.field_value
    if (!currentMessageType) return false
    const match = messageTypeOptions.find((o) => o.value === currentMessageType)
    const code = match?.code
    return code === "5" || code === "6"
  }, [invoice.extractedData, messageTypeOptions])

  const normalizedUnitOfMeasureSelectOptions = useMemo<FieldOption[]>(() => {
    const seen = new Set<string>()
    const dynamicOptions: FieldOption[] = []
    for (const unit of erpUnits) {
      if (seen.has(unit.code)) continue
      seen.add(unit.code)
      dynamicOptions.push({ label: `${unit.code} — ${unit.name}`, value: unit.code })
    }
    // Also include any values from line items that aren't in the dynamic list
    const fromLineItems: FieldOption[] = []
    for (const row of lineItemRows) {
      const v = (row.unitOfMeasure?.field_value ?? "").trim()
      if (v !== "" && !seen.has(v)) {
        seen.add(v)
        fromLineItems.push({ label: v, value: v })
      }
    }
    // Fallback: if no ERP units loaded, at least show PCE
    if (dynamicOptions.length === 0 && fromLineItems.length === 0) {
      return [{ label: "PCE", value: "PCE" }]
    }
    return [...dynamicOptions, ...fromLineItems]
  }, [erpUnits, lineItemRows])
  const draftUnitSelectValue = draftValues.unit === "" ? "__clear" : draftValues.unit
  const hasCustomDraftUnitValue =
    draftValues.unit !== "" &&
    !normalizedUnitOfMeasureSelectOptions.some((opt) => opt.value === draftValues.unit)

  const handleDatePickerSelect = useCallback(
    (fieldId: string, isoValue: string) => {
      handleFieldUpdateWithVies?.(fieldId, isoValue)
    },
    [handleFieldUpdateWithVies]
  )

  const handleFieldSave = useCallback(() => {
    fieldEditor.saveField(handleFieldUpdateWithVies)
  }, [fieldEditor, handleFieldUpdateWithVies])

  const handleFieldActivation = useCallback(
    (field: ExtractedField) => {
      if (fieldEditor.isEditing(field.id)) {
        return
      }

      fieldEditor.startEditing(field.id, getEditableValue(field))
    },
    [fieldEditor]
  )

  const handleFieldValueChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      fieldEditor.updateValue(event.target.value)
    },
    [fieldEditor]
  )

  const handleFieldSelectChange = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      handleFieldValueChange(event)
      fieldEditor.saveField(handleFieldUpdateWithVies)
    },
    [fieldEditor, handleFieldValueChange, handleFieldUpdateWithVies]
  )

  const handleFieldBlur = useCallback(
    (_event: FocusEvent<HTMLInputElement | HTMLSelectElement>, fieldId: string) => {
      if (!fieldEditor.isEditing(fieldId)) {
        return
      }

      handleFieldSave()
    },
    [fieldEditor, handleFieldSave]
  )

  const handleFieldInputKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
      fieldEditor.handleKeyDown(event, handleFieldUpdateWithVies)
    },
    [fieldEditor, handleFieldUpdateWithVies]
  )

  const editableFieldAttr = useMemo(
    () => ({
      [EDITABLE_FIELD_ATTR]: "true"
    }),
    []
  )

  const focusTargetAttr = useMemo(
    () => ({
      [FOCUS_TARGET_ATTR]: "field-input"
    }),
    []
  )

  const renderEditableValue = useMemo(
    () =>
      createEditableValueRenderer({
        fieldEditor,
        editableFieldAttr,
        focusTargetAttr,
        handleFieldActivation,
        handleFieldBlur,
        handleFieldInputKeyDown,
        handleFieldSelectChange,
        handleFieldValueChange,
        formatDisplayValue: formatFieldDisplayValue,
        onDatePickerSelect: handleDatePickerSelect
      }),
    [
      fieldEditor,
      editableFieldAttr,
      focusTargetAttr,
      handleFieldActivation,
      handleFieldBlur,
      handleFieldInputKeyDown,
      handleFieldSelectChange,
      handleFieldValueChange,
      handleDatePickerSelect
    ]
  )

  const renderField = useCallback(
    (targetField: ExtractedField) => {
      const lowerName = targetField.field_name.toLowerCase()
      const isVendorName = lowerName === "vendor_name"
      const isIncomeCustomerName = isIncome && lowerName === "customer_name"
      const isDropdown = ["payment_terms", "message_type", "additional_remarks", "vendor_country", "customer_country"].includes(lowerName)
      const fieldLocked = isExported || isFieldLockedByCounterparty(lowerName)

      // For counterparty name fields, render without outer label to avoid duplicates (FilterableSelect already handles it).
      if (isVendorName || isIncomeCustomerName) {
        return (
          <div
            key={targetField.id}
            className="w-full py-1"
          >
            <InvoiceFieldInput
              field={targetField}
              invoice={invoice}
              editableFieldAttr={editableFieldAttr}
              focusTargetAttr={focusTargetAttr}
              onFieldUpdate={handleFieldUpdateWithVies}
              onInvoiceRefresh={onInvoiceRefresh}
              handleSelectFieldChange={handleSelectFieldChange}
              renderEditableValue={renderEditableValue}
              messageTypeOptions={messageTypeOptions}
              documentSubjectOptions={documentSubjectOptions}
              isIncome={isIncome}
              readOnly={fieldLocked}
              exportLocked={isExported}
            />
          </div>
        )
      }

      const counterpartyPrefix = isIncome ? "customer" : "vendor"
      const requiredCounterpartyFields = new Set([
        `${counterpartyPrefix}_name`,
        `${counterpartyPrefix}_tax_id`,
        `${counterpartyPrefix}_country`,
      ])
      const isRequired =
        REQUIRED_INVOICE_FIELDS.has(lowerName) ||
        (!hasCounterparty && requiredCounterpartyFields.has(lowerName))
      const valueStr = (targetField.field_value ?? "").toString().trim()
      const isMissingRequired = isRequired && valueStr === ""

      const isTaxIdField = lowerName === "vendor_tax_id" || lowerName === "customer_tax_id"
      const viesTooltip = isTaxIdField
        ? getViestooltipText(targetField.extra_metadata?.vies_validation)
        : null

      // Render locked fields as read-only text (no click-to-edit)
      const fieldInput = fieldLocked ? (
        (() => {
          const hasValue =
            targetField.field_value !== undefined &&
            targetField.field_value !== null &&
            targetField.field_value !== ""
          // For select fields, resolve numeric value to human-readable label
          let displayValue = formatFieldDisplayValue(targetField)
          if (lowerName === "message_type" && messageTypeOptions.length > 0) {
            const match = messageTypeOptions.find(o => o.value === targetField.field_value)
            if (match) displayValue = match.label
          } else if (lowerName === "additional_remarks" && documentSubjectOptions.length > 0) {
            const match = documentSubjectOptions.find(o => o.value === targetField.field_value)
            if (match) displayValue = match.label
          }
          return (
            <div className="flex items-center gap-1 w-full min-w-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Lock className="h-3 w-3 flex-shrink-0 text-neutral-400" />
                </TooltipTrigger>
                <TooltipContent>{isExported ? "Locked — invoice is exported" : "Synced from Mirage"}</TooltipContent>
              </Tooltip>
              <span
                className="min-w-0 max-w-full text-left text-sm font-semibold text-neutral-950 leading-5 break-words whitespace-normal rounded px-1 py-1"
              >
                {hasValue ? displayValue : <span className="text-gray-400 italic">Not Available</span>}
              </span>
            </div>
          )
        })()
      ) : (
        <InvoiceFieldInput
          field={targetField}
          invoice={invoice}
          editableFieldAttr={editableFieldAttr}
          focusTargetAttr={focusTargetAttr}
          onFieldUpdate={handleFieldUpdateWithVies}
          onInvoiceRefresh={onInvoiceRefresh}
          handleSelectFieldChange={handleSelectFieldChange}
          renderEditableValue={renderEditableValue}
          messageTypeOptions={messageTypeOptions}
          documentSubjectOptions={documentSubjectOptions}

          isIncome={isIncome}
        />
      )

      const fieldContent = (
        <div
          key={targetField.id}
          aria-invalid={isMissingRequired || undefined}
          className={`items-center justify-end px-0 py-1 self-stretch w-full flex-[0_0_auto] 2xl:flex-1 flex ${isDropdown ? "" : "border-b border-neutral-200"} ${!fieldLocked && fieldEditor.isEditing(targetField.id) ? "bg-blue-50" : ""} ${isMissingRequired ? "border-l-2 border-l-red-500 pl-2" : ""}`}
        >
          <div className="flex flex-col items-start flex-1 max-w-full min-w-0">
            <div className="flex items-start w-full">
              <div className="flex flex-1 min-w-0 items-center 2xl:flex-col 2xl:items-start">
                <span className="order-2 h-5 truncate text-sm font-normal text-neutral-500 leading-5 whitespace-nowrap ml-4 capitalize 2xl:order-1 2xl:ml-0 2xl:w-full 2xl:whitespace-normal 2xl:overflow-visible 2xl:text-xs">
                  {targetField.field_name.replace(/_/g, " ")}
                  {isRequired && <span className={`ml-1 ${isMissingRequired ? "text-red-500" : "text-neutral-400"}`}>*</span>}
                </span>
                <div className="order-1 flex-1 min-w-0 2xl:order-2 2xl:w-full flex items-center gap-1">
                  {fieldInput}
                  {!fieldLocked && <OriginalValueIndicator field={targetField} />}
                  {isTaxIdField && (
                    <ViesValidationBadge
                      validation={targetField.extra_metadata?.vies_validation}
                      isValidating={validatingFieldIds.has(targetField.id)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )

      if (viesTooltip) {
        return (
          <Tooltip key={targetField.id}>
            <TooltipTrigger asChild>{fieldContent}</TooltipTrigger>
            <TooltipContent className="whitespace-pre-line">
              {viesTooltip}
            </TooltipContent>
          </Tooltip>
        )
      }

      return fieldContent
    },
    [
      documentSubjectOptions,
      editableFieldAttr,
      focusTargetAttr,
      handleSelectFieldChange,
      invoice,
      messageTypeOptions,
      handleFieldUpdateWithVies,
      onInvoiceRefresh,
      renderEditableValue,
      validatingFieldIds,
      isFieldLockedByCounterparty,
      fieldEditor,
      isIncome
    ]
  )

  const renderSectionTwoColumns = useCallback(
    (title: string, fields: ExtractedField[], isEmpty?: boolean) => (
      <Card key={title} className="w-full border border-neutral-200 shadow-sm">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <h3 className="font-text-sm-medium text-black">{title}</h3>
            <span className="text-xs text-gray-500">({fields.length} fields)</span>
          </div>
          {isEmpty ? (
            <div className="px-2 py-4 text-sm text-gray-400 italic text-center">
              No {title.toLowerCase()} data available
            </div>
          ) : (
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-x-6">
              {fields.map(renderField)}
            </div>
          )}
        </CardContent>
      </Card>
    ),
    [renderField]
  )

  return (
    <>
      {/* Row 1 Col 2: Counterparty Information */}
      <div className="2xl:col-span-1">
        <CounterpartyAssignmentCard
          title="Counterparty Information"
          fields={isIncome ? categorizedData.customer : categorizedData.vendor}
          renderField={renderField}
        />
      </div>

      {/* Row 2: Invoice Information - full width with 2-column internal layout */}
      <div className="2xl:col-span-2">
        {renderSectionTwoColumns("Invoice Information", categorizedData.invoice, categorizedData.invoice.length === 0)}
      </div>

      {/* Row 2.25: Original Document — only for credit/debit notes */}
      {isCreditOrDebitNote && (
        <div className="2xl:col-span-2">
          <OriginalDocPicker
            invoiceId={invoice.id}
            currentNumber={invoice.original_invoice_number ?? null}
            currentDate={invoice.original_invoice_date ?? null}
            currentSaldoInvoiceId={invoice.original_invoice_id ?? null}
            currentMirageDocHeaderId={invoice.mirage_original_doc_header_id ?? null}
            readOnly={isExported}
            onLinkChanged={() => onInvoiceRefresh?.()}
          />
        </div>
      )}

      {/* Row 2.5: Currency & Amounts */}
      <div className="2xl:col-span-2">
        <Card className="border border-neutral-200 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-text-sm-medium text-neutral-800 mb-4">Currency & Amounts</h3>
            <div className="space-y-4">
              {onInvoiceRefresh && (
                <RedesignedCurrencyField
                  invoice={invoice}
                  onInvoiceRefresh={async () => { await Promise.resolve(onInvoiceRefresh()) }}
                  readOnly={isExported}
                />
              )}
              {(() => {
                const extractedSubtotal = invoice.extractedData?.find((f) => f.field_name === "subtotal")?.field_value
                const extractedTax = invoice.extractedData?.find((f) => f.field_name === "tax_amount")?.field_value
                const extractedTotal = invoice.extractedData?.find((f) => f.field_name === "total_amount")?.field_value
                const hasExtracted = extractedSubtotal || extractedTax || extractedTotal

                const differs = (calc: string | null | undefined, ext: string | null | undefined): boolean => {
                  if (!calc || !ext) return false
                  const c = parseFloat(calc)
                  const e = parseFloat(ext)
                  return !isNaN(c) && !isNaN(e) && Math.abs(c - e) >= 0.005
                }

                const subtotalDiffers = differs(activeRepr?.subtotal, extractedSubtotal)
                const taxDiffers = differs(activeRepr?.tax_amount, extractedTax)
                const totalDiffers = differs(activeRepr?.total_amount, extractedTotal)

                const ccy = invoice.active_currency_code || ''
                const withCcy = (v: string | null | undefined) =>
                  v && v !== '—' ? (ccy ? `${v} ${ccy}` : v) : '—'

                return (
                  <div className="space-y-0.5">
                    {/* Column headers */}
                    <div className="grid grid-cols-[80px_160px_160px_160px] gap-x-6 items-end">
                      <div />
                      <label className="text-sm font-normal text-neutral-500 leading-5 2xl:text-xs">Subtotal</label>
                      <label className="text-sm font-normal text-neutral-500 leading-5 2xl:text-xs">Tax Amount</label>
                      <label className="text-sm font-normal text-neutral-500 leading-5 2xl:text-xs">Total Amount</label>
                    </div>

                    {/* Calculated row */}
                    <div className="grid grid-cols-[80px_160px_160px_160px] gap-x-6 items-center">
                      <span className="text-sm font-normal text-neutral-500 leading-5 2xl:text-xs">Calculated</span>
                      <div className="text-sm text-neutral-900">{withCcy(activeRepr?.subtotal)}</div>
                      <div className="text-sm text-neutral-900">{withCcy(activeRepr?.tax_amount)}</div>
                      <div className="text-sm font-semibold text-neutral-900">{withCcy(activeRepr?.total_amount)}</div>
                    </div>

                    {/* Extracted row */}
                    {hasExtracted && (
                      <div className="grid grid-cols-[80px_160px_160px_160px] gap-x-6 items-center">
                        <span className="text-sm font-normal text-neutral-500 leading-5 2xl:text-xs">Extracted</span>
                        <div className={`text-sm ${subtotalDiffers ? "text-red-600 font-semibold animate-pulse" : "text-neutral-500"}`}>
                          {withCcy(extractedSubtotal)}
                        </div>
                        <div className={`text-sm ${taxDiffers ? "text-red-600 font-semibold animate-pulse" : "text-neutral-500"}`}>
                          {withCcy(extractedTax)}
                        </div>
                        <div className={`text-sm ${totalDiffers ? "text-red-600 font-semibold animate-pulse" : "text-neutral-500"}`}>
                          {withCcy(extractedTotal)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Line Items - spans full width */}
      <div className="2xl:col-span-2">
        <LineItemsCard
          lineItemRows={lineItemRows}
          selectedLineItemIds={selectedLineItemIds}
          areAllLineItemsSelected={areAllLineItemsSelected}
          normalizedUnitOfMeasureSelectOptions={normalizedUnitOfMeasureSelectOptions}
          editableFieldAttr={editableFieldAttr}
          focusTargetAttr={focusTargetAttr}
          hasSelection={hasSelection}
          hasMergeableSelection={hasMergeableSelection}
          isMergePending={isMergePending}
          clientId={clientId}
          invoiceId={invoice.id}
          lineItemsErpMetadata={lineItemsErpMetadata}
          onClearSelection={clearSelection}
          onAddLineItem={handleAddLineItem}
          onMergeSelected={handleMergeSelected}
          onRowSelectionChange={handleRowSelectionChange}
          onSelectAllLineItems={handleSelectAllLineItems}
          onDeleteRow={handleDeleteRow}
          onUnitChange={handleSelectFieldChange}
          onFieldUpdate={handleFieldUpdateWithVies}
          onRepresentationUpdate={onRepresentationUpdate}
          onLineItemErpMetadataChange={handleLineItemErpMetadataChange}
          onLineItemsValidityChange={onLineItemsValidityChange}
          onRestoreLineItems={onRestoreLineItems}
          mergedGroupIds={mergedGroupIds}
          renderEditableValue={renderEditableValue}
          isIncome={isIncome}
          readOnly={isExported}
          actionType={
            invoice.invoice_type === "purchase"
              ? 103
              : invoice.invoice_type === "sales"
                ? 102
                : undefined
          }
        />
      </div>

      {isDrawerOpen && (
        <div className="fixed right-0 top-0 z-[60] flex h-screen w-full max-w-xl flex-col gap-6 rounded-none border-l border-neutral-200 bg-white p-6 shadow-xl animate-in slide-in-from-right-full duration-200">
          <div className="space-y-1">
            <h2 className="text-lg font-semibold leading-none tracking-tight">
              {drawerMode === "merge" ? "Merge selected line items" : "Add new line item"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {drawerMode === "merge"
                ? `Combine ${selectedRows.length} line item${selectedRows.length === 1 ? "" : "s"} into a single entry.`
                : "Create a custom line item that will be sent to the backend as part of the invoice."}
            </p>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-6 -mx-6 px-6">
          {drawerMode === "merge" && selectedRows.length > 0 && (
            <div className="rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
              <p className="text-xs font-semibold text-neutral-700">Selected rows</p>
              <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                {selectedRows.map((row) => (
                  <li key={row.id} className="flex items-center justify-between gap-2">
                    <span className="flex-1 truncate">{row.description?.field_value ?? "—"}</span>
                    <span className="text-neutral-500">
                      {row.lineTotal ?? row.quantity ?? "—"}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mergeConflictMessages.length > 0 && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              {mergeConflictMessages.map((message, index) => (
                <p key={index}>{message}</p>
              ))}
            </div>
          )}

          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-medium text-neutral-600" htmlFor="line-item-description">
                Description
              </label>
              <textarea
                id="line-item-description"
                className="h-24 w-full resize-none rounded-md border border-input bg-transparent px-3 py-2 text-sm text-neutral-900 shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={draftValues.description}
                onChange={(event) => handleDraftFieldChange("description", event.target.value)}
                placeholder="Describe the merged line item"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-neutral-600" htmlFor="line-item-quantity">
                  Quantity
                </label>
                <Input
                  id="line-item-quantity"
                  value={draftValues.quantity}
                  onChange={(event) => handleDraftFieldChange("quantity", event.target.value.replace(/,/g, "."))}
                  placeholder="e.g. 5"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-neutral-600" htmlFor="line-item-unit-price">
                  Unit price
                </label>
                <Input
                  id="line-item-unit-price"
                  value={draftValues.unitPrice}
                  onChange={(event) => handleDraftFieldChange("unitPrice", event.target.value.replace(/,/g, "."))}
                  placeholder="e.g. 19.99"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-neutral-600" htmlFor="line-item-vat-rate">
                  VAT %
                </label>
                <Input
                  id="line-item-vat-rate"
                  value={draftValues.vatRate}
                  onChange={(event) => handleDraftFieldChange("vatRate", event.target.value.replace(/,/g, "."))}
                  placeholder="e.g. 20"
                />
              </div>
              <div className="space-y-1 col-span-2">
                <label className="text-xs font-medium text-neutral-600" htmlFor="line-item-unit">
                  Unit
                </label>
                <Select
                  value={draftUnitSelectValue}
                  onValueChange={(value) => handleDraftFieldChange("unit", value === "__clear" ? "" : value)}
                >
                  <SelectTrigger
                    id="line-item-unit"
                    className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm text-neutral-900 shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  >
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="__clear" className="cursor-pointer">
                      No value
                    </SelectItem>
                    {normalizedUnitOfMeasureSelectOptions.map((option, index) => (
                      <SelectItem
                        key={option.value || option.label || `draft-unit-${index}`}
                        value={option.value}
                        className="cursor-pointer"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                    {hasCustomDraftUnitValue && (
                      <SelectItem value={draftValues.unit} className="cursor-pointer">
                        {draftValues.unit}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {drawerMode === "merge" && clientId && (
            <MergeDrawerErpFields
              clientId={clientId}
              isIncome={isIncome}
              erpDraft={draftValues.erp}
              onErpDraftChange={handleErpDraftChange}
            />
          )}
          </div>

          <div className="mt-auto flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              type="button"
              onClick={() => handleDrawerOpenChange(false)}
              disabled={Boolean(pendingActionType)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="blue"
              onClick={handleDrawerSubmit}
              disabled={
                !drawerMode || (drawerMode === "merge" && pendingActionType === "merge")
              }
            >
              {drawerMode === "merge"
                ? pendingActionType === "merge"
                  ? "Merging..."
                  : "Save merged line item"
                : "Add line item"}
            </Button>
          </div>
        </div>
      )}
    </>
  )
}
